import React, { useEffect, useState } from "react";
import { Modal, Button, Table } from "react-bootstrap";
import Swal from "sweetalert2";
import QuatationServices from "../../../services/QuatationServices";
import PoServices from "../../../services/PoServices";

const ManualQuotationModal = ({ show, handleClose, row }) => {

  const [suppliers, setSuppliers] = useState([]);
  const [entries, setEntries] = useState({});

  // ================================
  // Load suppliers
  // ================================
  useEffect(() => {
    const loadSuppliers = async () => {
      try {
        const res = await PoServices.activeSuppliers();
        setSuppliers(res?.data || []);
      } catch (err) {
        console.error("Supplier load failed", err);
      }
    };
    loadSuppliers();
  }, []);

  // ================================
  // Init parts
  // ================================
  useEffect(() => {
    if (!show || !row?.items?.length) return;

    const obj = {};
    row.items.forEach(it => {
      const partNo = it.inventory?.masterItem?.partNo;
      if (partNo) obj[partNo] = [];
    });

    setEntries(obj);
  }, [show, row]);

  // ================================
  // Add row
  // ================================
  const addSupplierRow = (partNo) => {
    setEntries(prev => ({
      ...prev,
      [partNo]: [
        ...(prev[partNo] || []),
        { supplierId: "", unit: "", rate: 0 }
      ]
    }));
  };

  // ================================
  // Update row (force types)
  // ================================
  const updateRow = (partNo, index, field, value) => {
    setEntries(prev => {
      const updated = [...(prev[partNo] || [])];

      let finalValue = value;

      if (field === "unit") {
        finalValue = String(value).trim();      // ✅ always string
      }

      if (field === "rate") {
        finalValue = parseFloat(value) || 0;    // ✅ always float
      }

      updated[index] = { ...updated[index], [field]: finalValue };

      return { ...prev, [partNo]: updated };
    });
  };

  // ================================
  // Prevent duplicate suppliers
  // ================================
  const usedSuppliers = (partNo) =>
    (entries[partNo] || []).map(r => r.supplierId).filter(Boolean);

  // ================================
  // Validate before new row
  // ================================
  const canAddNewRow = (partNo) => {
    const rows = entries[partNo] || [];

    if (!rows.length) return true;

    const last = rows[rows.length - 1];

    return (
      last.supplierId &&
      typeof last.unit === "string" &&
      last.unit.length > 0 &&
      typeof last.rate === "number" &&
      last.rate > 0
    );
  };

  // ================================
  // Submit
  // ================================
 const handleSubmit = async () => {

  const replies = [];

  // ===============================
  // Build replies from form
  // ===============================
  for (const partNo in entries) {
    for (const rowData of entries[partNo]) {

      if (!rowData.supplierId || !rowData.unit || !rowData.rate) {
        return Swal.fire(
          "Validation Error",
          `Fill all fields for part ${partNo}`,
          "warning"
        );
      }

      replies.push({
        supplierId: rowData.supplierId,
        partNo,
        unit: String(rowData.unit),
        rate: parseFloat(rowData.rate)
      });
    }
  }

  if (!replies.length) {
    return Swal.fire("No Data", "Add at least one quotation", "warning");
  }

  // ===============================
  // Extract ONLY selected suppliers
  // ===============================
  const selectedSuppliers = Array.from(
    new Map(
      replies.map(r => {
        const s = suppliers.find(x => x._id === r.supplierId);

        return [
          r.supplierId,
          {
            supplierId: s._id,
            name: s.name,
            phone: s.mobileNo?.[0] || ""
          }
        ];
      })
    ).values()
  );

  // ===============================
  // Build payload (same as WhatsApp flow)
  // ===============================
  const payload = {
    requisitionId: row?.requisitionId || row?._id,
    requisitionNo: row?.requisitionNo,

    items: row.items.map(it => ({
      partNo: it.inventory.masterItem.partNo,
      description: it.inventory.masterItem.description,
      requiredQty: it.qtyRequired
    })),

    suppliers: selectedSuppliers,   // ✅ only chosen ones
    replies
  };

  // ===============================
  // Submit
  // ===============================
  try {
    await QuatationServices.addManualRate(payload);

    Swal.fire("Success", "Quotation logged & captured successfully", "success");

    handleClose();

  } catch (err) {
    console.error(err);
    Swal.fire("Error", "Save failed", "error");
  }
};



  if (!row?.items?.length) return null;

  return (
    <Modal show={show} onHide={handleClose} size="xl" enforceFocus={false}>
      <Modal.Header closeButton>
        <Modal.Title>Manual Quotation Entry</Modal.Title>
      </Modal.Header>

      <Modal.Body>

        <Table bordered responsive size="sm">
          <thead>
            <tr>
              <th>Part No</th>
              <th>Description</th>
              <th>Supplier Quotations</th>
            </tr>
          </thead>

          <tbody>
            {row.items.map((it, idx) => {
              const partNo = it.inventory?.masterItem?.partNo;
              const desc = it.inventory?.masterItem?.description;

              return (
                <tr key={idx}>
                  <td className="fw-bold">{partNo}</td>
                  <td>{desc}</td>

                  <td>
                    {(entries[partNo] || []).map((r, i) => (
                      <div key={i} className="d-flex gap-2 mb-2">

                        {/* Supplier */}
                        <select
                          className="form-control"
                          value={r.supplierId}
                          onChange={e =>
                            updateRow(partNo, i, "supplierId", e.target.value)
                          }
                        >
                          <option value="">Select Supplier</option>
                          {suppliers
                            .filter(s =>
                              !usedSuppliers(partNo).includes(s._id) ||
                              s._id === r.supplierId
                            )
                            .map(s => (
                              <option key={s._id} value={s._id}>
                                {s.name}
                              </option>
                            ))}
                        </select>

                        {/* Unit (string) */}
                        <input
                          className="form-control"
                          placeholder="Unit"
                          value={r.unit}
                          onChange={e =>
                            updateRow(partNo, i, "unit", e.target.value)
                          }
                        />

                        {/* Rate (float) */}
                        <input
                          type="number"
                          step="0.01"
                          className="form-control"
                          placeholder="Rate"
                          value={r.rate || ""}
                          onChange={e =>
                            updateRow(partNo, i, "rate", e.target.value)
                          }
                        />
                      </div>
                    ))}

                    <Button
                      size="sm"
                      variant="outline-primary"
                      onClick={() => {
                        if (!canAddNewRow(partNo)) {
                          Swal.fire(
                            "Incomplete Row",
                            "Please fill Supplier, Unit and Rate before adding a new row.",
                            "warning"
                          );
                          return;
                        }
                        addSupplierRow(partNo);
                      }}
                    >
                      + Add Supplier
                    </Button>

                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>

      </Modal.Body>

      <Modal.Footer className="justify-content-center">
        <Button variant="success" onClick={handleSubmit}>
          Save Quotations
        </Button>
        <Button variant="secondary" onClick={handleClose}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ManualQuotationModal;
